import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

export default function TermsBuyer() {
  return (
    <LegalLayout title="Buyer Terms of Service" eyebrow="Legal · Buyers" lastUpdated="1 March 2026" version="1.0">
      <p>
        These Buyer Terms of Service ("<strong>Terms</strong>") form a legally binding agreement between{" "}
        <strong>Connexis Group Ltd</strong>, a company registered in England and Wales with company number{" "}
        <strong>17214446</strong> ("<strong>Connexis</strong>"), and the business entity (the "<strong>Buyer</strong>")
        accessing the Connexis platform to purchase consumer enquiries ("<strong>Leads</strong>") under a prepaid
        wallet model.
      </p>
      <p>
        By creating a buyer account, topping up your wallet, or accepting delivery of any Lead, the Buyer agrees to
        be bound by these Terms.
      </p>

      <h2>1. The service</h2>
      <p>
        Connexis operates infrastructure that matches consumer enquiries from its own funnels, affiliates and
        publisher network with vetted buyers in real time. Each Lead is delivered to <strong>exactly one buyer</strong>{" "}
        (exclusive, never resold) at the fixed sector price per lead ("<strong>CPL</strong>") published on the
        Connexis pricing page.
      </p>

      <h2>2. Wallet &amp; payment</h2>
      <ul>
        <li>All commercials are <strong>prepaid</strong>. The Buyer tops up a wallet via Stripe Checkout or bank transfer (with a 20% VAT invoice).</li>
        <li>Lead acceptance debits the wallet at the applicable sector CPL. When the wallet balance falls below the next-lead CPL, delivery is paused automatically.</li>
        <li>The Buyer may pause delivery at any time from the portal.</li>
        <li>Wallet balances are held by Connexis as a prepayment against future Leads and are non-refundable except where required by law or where Connexis cancels the account without cause.</li>
      </ul>

      <h2>3. Lead quality &amp; returns</h2>
      <ul>
        <li>Connexis warrants that each delivered Lead has a recorded consent token (TrustedForm or equivalent) and meets the sector consent requirements.</li>
        <li>The Buyer may <strong>return</strong> a Lead within 72 hours of delivery for a documented quality issue (e.g. invalid contact details, duplicate within 7 days, fraudulent submission).</li>
        <li>Accepted returns are refunded to the Buyer's wallet automatically.</li>
        <li>Quality disputes that cannot be resolved in-portal are referred to the Connexis Compliance Team and resolved within 5 business days.</li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>The Buyer must:</p>
      <ul>
        <li>Hold all required FCA / regulatory permissions for the sector(s) in which they accept Leads.</li>
        <li>Honour the consent on each Lead (do not contact outside of consented purposes).</li>
        <li>Comply with UK GDPR, PECR, TCPA (where applicable) and any sector-specific rules (CONC, ICOBS, MCOB).</li>
        <li>Not resell, share, syndicate or otherwise distribute any Lead to a third party.</li>
      </ul>

      <h2>5. Service levels</h2>
      <ul>
        <li>Target platform uptime: <strong>99.9%</strong>, measured monthly.</li>
        <li>Lead routing median latency: <strong>≤200ms</strong>.</li>
        <li>Webhook delivery: auto-retry with exponential back-off up to 24 hours.</li>
      </ul>

      <h2>6. Confidentiality &amp; data</h2>
      <p>
        Each party shall keep confidential all non-public information received from the other. Personal data shared
        in connection with this agreement is processed under the joint controllership arrangement described in our{" "}
        <a href="/legal/dpa">Data Processing Agreement</a>.
      </p>

      <h2>7. Term &amp; termination</h2>
      <ul>
        <li>This agreement runs for an initial term of 12 months from first wallet top-up and renews automatically for successive 12-month periods unless terminated.</li>
        <li>Either party may terminate for convenience on 30 days written notice.</li>
        <li>Connexis may suspend or terminate the Buyer's account immediately for breach of clause 4 (Acceptable Use) or non-payment.</li>
      </ul>

      <h2>8. Liability</h2>
      <p>
        Neither party is liable for indirect, consequential or punitive damages. Connexis's aggregate liability under
        this agreement is capped at <strong>the total wallet spend by the Buyer in the 12 months preceding the
        claim</strong>. Nothing in this clause limits liability for death, personal injury, fraud or wilful
        misconduct.
      </p>

      <h2>9. Governing law</h2>
      <p>
        These Terms are governed by the laws of England and Wales. The parties submit to the exclusive jurisdiction
        of the courts of England and Wales.
      </p>

      <h2>10. Notices</h2>
      <p>
        Notices to Connexis: <a href="mailto:legal@connex.is">legal@connex.is</a><br />
        Notices to the Buyer: the email address registered on the buyer's account.
      </p>

      <p className="!mt-12 !text-[13px] !text-slate-500">
        These Terms are signed on behalf of Connexis Group Ltd by its appointed director. The Buyer's authorised
        signatory accepts these Terms by completing the e-signature flow in the buyer portal.
      </p>
    </LegalLayout>
  );
}
